import { useCallback, useEffect, useRef, useState } from "react";
import { FlatList } from "react-native";
import { SpotlightPagination, SpotlightPaginationItem } from "./types";

type Params = {
  pagination: SpotlightPagination;
  visible: boolean;
  onClose?: () => void;
};

export default function useSpotlightNavigation({
  pagination,
  visible,
  onClose,
}: Params) {
  const flatListRef = useRef<FlatList<SpotlightPaginationItem>>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (visible) setCurrentIndex(0);
  }, [visible]);

  const total = pagination.items.length;

  useEffect(() => {
    setCurrentIndex((prev) =>
      prev > total - 1 ? Math.max(total - 1, 0) : prev
    );
  }, [total]);

  const isCarousel = total > 1;

  const handleForward = useCallback(() => {
    if (!pagination.items[currentIndex]) return;
    pagination.onNext?.(
      pagination.items[currentIndex],
      pagination.items[currentIndex + 1]
    );
    if (currentIndex === pagination.items.length - 1) {
      onClose?.();
      return;
    }
    setCurrentIndex((prev) => prev + 1);
    flatListRef.current?.scrollToIndex({
      index: currentIndex + 1,
      animated: true,
    });
  }, [pagination, currentIndex, onClose]);

  const handleBack = useCallback(() => {
    if (currentIndex === 0) return;
    pagination.onPrev?.();
    setCurrentIndex((prev) => prev - 1);
    flatListRef.current?.scrollToIndex({
      index: currentIndex - 1,
      animated: true,
    });
  }, [pagination, currentIndex]);

  return {
    flatListRef,
    total,
    currentIndex,
    isFirst: currentIndex === 0,
    isLast: currentIndex === total - 1,
    isCarousel,
    activeItem: pagination.items[currentIndex],
    onForward: handleForward,
    onBack: handleBack,
  };
}
